import { isEqual } from '@sequencehq/utils/dates'
import { useSetAtom, useAtomValue } from 'jotai'
import { useCallback, useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import {
  QUOTE_SEMANTIC_TYPES,
  useAutomationSimulationContext
} from 'Automations/simulation'
import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import { CubeDomainInterface } from 'modules/Cube/domain/cube.domain'
import { CubeStatus } from 'modules/Cube/domain/cube.domain.types'

import { quoteSyncStatusAtom, type QuoteSyncStatus } from './quoteSync.atoms'
import {
  logQuoteNeedsSync,
  logQuoteSaveTriggered,
  logQuoteSyncComplete
} from './quoteSyncDeveloperLog'
import { shouldSyncQuote } from './shouldSyncQuote'

/**
 * Keeps the quote saved as it's edited, and says where that has got to through
 * `quoteSyncStatusAtom` so anything below the provider can wait on it.
 *
 * Mount this once per editor - it owns the debounce and the comparison, so a
 * second copy would save everything twice.
 */
export const useQuoteSync = (): { status: QuoteSyncStatus } => {
  const cubeContext = useCubeContext()
  const simulation = useAutomationSimulationContext()
  const status = useAtomValue(quoteSyncStatusAtom)
  const setStatus = useSetAtom(quoteSyncStatusAtom)
  const [previousData, setPreviousData] = useState<
    CubeDomainInterface['queries']['rawData']['data'] | undefined
  >()
  const [previousLastLoadedAt, setLastLoadedAt] = useState<Date | undefined>()

  /**
   * A save already on its way stays SYNCING: it's still going, and the header
   * shouldn't drop its spinner just because another edit queued behind it. The
   * queued one saves on the next debounce.
   */
  const queueSync = useCallback(
    () => setStatus(current => (current === 'SYNCING' ? current : 'PENDING')),
    [setStatus]
  )

  const persistChanges = useDebouncedCallback(async () => {
    const startedAt = Date.now()
    const quoteStatus = cubeContext.queries.rawData.data.common.status
    const reload =
      quoteStatus === CubeStatus.QuotePublished ||
      quoteStatus === CubeStatus.QuoteReadyToSign

    logQuoteSaveTriggered({ reload })
    setStatus('SYNCING')
    await cubeContext.mutators.external.out.save({
      reload,
      showValidationErrors: false
    })
    setStatus('IDLE')
    logQuoteSyncComplete({ elapsedMs: Date.now() - startedAt })

    // Only once the save has come back. The simulation reads the quote, so
    // asking any earlier hands it the version we've just replaced.
    await simulation.refetchDependencies(QUOTE_SEMANTIC_TYPES)
  }, 2000)

  useEffect(() => {
    /**
     * Only allow autosaving on draft and published quotes
     */
    if (!cubeContext.queries.availableFeatures.quote.edit.available.enabled) {
      /**
       * Allow the start date or attachments to be updated at any point before
       * executing a quote.
       */
      const startDateChanged =
        previousData &&
        previousData?.common.startDate?.toISOString() !==
          cubeContext.queries.rawData.data.common.startDate?.toISOString()
      const attachmentsChanged =
        previousData &&
        cubeContext.queries.rawData.data.quote.attachmentAssets.length !==
          previousData.quote.attachmentAssets.length

      if (
        [CubeStatus.QuoteAccepted, CubeStatus.QuoteReadyToSign].includes(
          cubeContext.queries.rawData.data.common.status
        ) &&
        (startDateChanged || attachmentsChanged)
      ) {
        queueSync()
        void persistChanges()
      }

      /**
       * Allow salesforce opportunity id to be updated after accepting or executing a quote
       */
      if (
        [CubeStatus.QuoteAccepted, CubeStatus.QuoteExecuted].includes(
          cubeContext.queries.rawData.data.common.status
        ) &&
        previousData?.quote &&
        previousData.quote.salesforceOpportunityId !==
          cubeContext.queries.rawData.data.quote.salesforceOpportunityId
      ) {
        queueSync()
        void persistChanges()
      }

      setPreviousData(cubeContext.queries.rawData.data)

      return
    }

    if (
      !previousLastLoadedAt ||
      !cubeContext.queries.rawData.editor.lastLoadedAt ||
      !isEqual(
        previousLastLoadedAt,
        cubeContext.queries.rawData.editor.lastLoadedAt
      )
    ) {
      setPreviousData(undefined)
      setLastLoadedAt(cubeContext.queries.rawData.editor.lastLoadedAt)
      return
    }

    if (
      !previousData &&
      previousLastLoadedAt &&
      cubeContext.queries.rawData.editor.lastLoadedAt &&
      isEqual(
        previousLastLoadedAt,
        cubeContext.queries.rawData.editor.lastLoadedAt
      )
    ) {
      setPreviousData(cubeContext.queries.rawData.data)
      return
    }

    const isPreviousDataUpToDateWithServer = Boolean(
      previousLastLoadedAt &&
        cubeContext.queries.rawData.editor.lastLoadedAt &&
        isEqual(
          previousLastLoadedAt,
          cubeContext.queries.rawData.editor.lastLoadedAt
        )
    )

    const { needsSync, reasons } = shouldSyncQuote({
      current: cubeContext.queries.rawData.data,
      previous: previousData,
      isAlreadySaving: cubeContext.queries.rawData.editor.savingSchedule,
      isPreviousDataUpToDateWithServer
    })

    if (!needsSync) {
      return
    }

    logQuoteNeedsSync({
      reasons,
      previous: previousData,
      current: cubeContext.queries.rawData.data,
      previousLastLoadedAt,
      currentLastLoadedAt: cubeContext.queries.rawData.editor.lastLoadedAt
    })

    setPreviousData(cubeContext.queries.rawData.data)
    queueSync()

    if (
      cubeContext.queries.rawData.data.common.status ===
        CubeStatus.QuotePublished ||
      cubeContext.queries.rawData.data.common.status ===
        CubeStatus.QuoteReadyToSign
    ) {
      cubeContext.mutators.updateData({
        common: {
          status: CubeStatus.QuoteDraft
        }
      })
    }

    void persistChanges()
  }, [
    cubeContext.queries.rawData.editor.savingSchedule,
    cubeContext.queries.rawData.data,
    previousData,
    cubeContext.mutators,
    cubeContext.queries.rawData.editor.lastLoadedAt,
    previousLastLoadedAt,
    persistChanges,
    queueSync,
    cubeContext.queries.availableFeatures.quote.edit.available.enabled
  ])

  return { status }
}
